import { type Prettify } from "@oefr/utils/types"
import { ConsentCode, isAcceptedConsent } from "@/entities/consents"
import { type FragmentOf, graphql, readFragment, type ResultOf } from "@/graphql"

const graphQLFragments = {
    accountUser: graphql(
        `
            fragment hasAcceptedMarketingConsents_AccountUser on AccountUserType {
                consents {
                    ...isAcceptedConsent_Consent
                }
                preferences {
                    isOptedInToSmsMessages
                }
            }
        `,
        [isAcceptedConsent.fragments.consent]
    ),
}

type AccountUser = ResultOf<typeof graphQLFragments.accountUser>

type MarketingConsent = "smsOptin" | ConsentCode

const ALL_MARKETING_CONSENTS: MarketingConsent[] = ["smsOptin", ...Object.values(ConsentCode)]

function accountUserHasConsent(accountUser: AccountUser, consent: MarketingConsent) {
    switch (consent) {
        case "smsOptin":
            return !!accountUser.preferences?.isOptedInToSmsMessages
        default:
            return !!accountUser.consents?.some((userConsent) =>
                isAcceptedConsent(userConsent, consent)
            )
    }
}

export type HasAcceptedMarketingConsentsOptions = Prettify<
    {
        match?: "some" | "every"
    } & (
        | {
              include?: readonly MarketingConsent[]
              exclude?: never
          }
        | {
              include?: never
              exclude?: readonly MarketingConsent[]
          }
    )
>

/**
 * Checks the selected marketing consents.
 * Use `include` to check only these consents, or `exclude` to check everything but these.
 * Without options, every consent code and the SMS opt-in are checked.
 * Use `match` to choose between `"every"` (default: all of them are accepted)
 * and `"some"` (at least one is accepted).
 *
 * @example
 * // SMS and commercial communication both accepted
 * hasAcceptedMarketingConsents(accountUser, {
 *     include: ["smsOptin", ConsentCode.MARKETING_COMMERCIAL_COMMUNICATION],
 * })
 *
 * @example
 * // SMS or commercial communication accepted
 * hasAcceptedMarketingConsents(accountUser, {
 *     include: ["smsOptin", ConsentCode.MARKETING_COMMERCIAL_COMMUNICATION],
 *     match: "some",
 * })
 *
 * @example
 * // Any consent accepted, except personal communication
 * hasAcceptedMarketingConsents(accountUser, {
 *     exclude: [ConsentCode.MARKETING_PERSONAL_COMMUNICATION],
 *     match: "some",
 * })
 */
export function hasAcceptedMarketingConsents(
    fragment: FragmentOf<typeof graphQLFragments.accountUser>,
    {
        include = ALL_MARKETING_CONSENTS,
        exclude = [],
        match = "every",
    }: HasAcceptedMarketingConsentsOptions = {}
) {
    const accountUser = readFragment(graphQLFragments.accountUser, fragment)

    return include
        .filter((consent) => !exclude.includes(consent))
        [match]((consent) => accountUserHasConsent(accountUser, consent))
}

hasAcceptedMarketingConsents.fragments = graphQLFragments
